<script setup lang="ts" generic="TData">
import { FlexRender, type HeaderGroup } from '@tanstack/vue-table';

interface Props {
  headerGroups: HeaderGroup<TData>[];
  sortable: boolean;
  getColumnStyle: (headerId: string) => Record<string, string> | undefined;
}

defineProps<Props>();

const TEXT_ALIGN = { center: 'text-center', end: 'text-right' } as const;
const JUSTIFY = { center: 'justify-center', end: 'justify-end' } as const;

defineSlots<{
  [key: `header-${string}`]: (props: { header: unknown; column: unknown }) => unknown;
}>();
</script>

<template>
  <thead class="base-table__thead">
    <tr
      v-for="headerGroup in headerGroups"
      :key="headerGroup.id"
      class="base-table__header-row"
    >
      <th
        v-for="header in headerGroup.headers"
        :key="header.id"
        :colspan="header.colSpan"
        :style="getColumnStyle(header.id)"
        :class="[
          'base-table__th',
          header.column.columnDef.meta?.align && TEXT_ALIGN[header.column.columnDef.meta.align],
          {
            'base-table__th--sortable': sortable && header.column.getCanSort(),
            'base-table__th--sorted': sortable && header.column.getIsSorted(),
          },
        ]"
        @click="sortable ? header.column.getToggleSortingHandler()?.($event) : undefined"
      >
        <div
          class="base-table__header-content"
          :class="header.column.columnDef.meta?.align && JUSTIFY[header.column.columnDef.meta.align]"
        >
          <!-- Custom header slot -->
          <slot
            v-if="$slots[`header-${header.column.id}`]"
            :name="`header-${header.column.id}`"
            :header="header"
            :column="header.column"
          />
          <!-- Default header -->
          <FlexRender
            v-else
            :render="header.column.columnDef.header"
            :props="header.getContext()"
          />
          <!-- Sort indicator -->
          <span
            v-if="sortable && header.column.getCanSort()"
            class="base-table__sort-indicator"
          >
            <template v-if="header.column.getIsSorted() === 'asc'"> ↑ </template>
            <template v-else-if="header.column.getIsSorted() === 'desc'"> ↓ </template>
            <template v-else> ⇅ </template>
          </span>
        </div>
      </th>
    </tr>
  </thead>
</template>

<style scoped>
@reference "../../../../tailwind.css";

@layer components {
  .base-table__thead {
    background-color: var(--base-header-bg);
    color: var(--base-text-color);
  }

  .base-table__header-row {
    border-bottom: 1px solid var(--booking-chessboard-table-border-color);
  }

  .base-table__th--sortable {
    @apply cursor-pointer select-none;
  }

  .base-table__th--sortable:hover {
    background-color: var(--base-secondary-color);
  }

  .base-table__header-content {
    @apply flex items-center gap-2;
  }

  .base-table__sort-indicator {
    @apply text-xs opacity-50;
  }

  .base-table__th--sorted .base-table__sort-indicator {
    @apply opacity-100;
    color: var(--base-accent-color);
  }
}
</style>
